import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button, Eyebrow } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check, ArrowRight } from 'lucide-react'
import type { Competitor } from '@/data/compare'
import { competitors } from '@/data/compare'

export function ComparisonPageTemplate({ competitor }: { competitor: Competitor }) {
  const url = `/compare/${competitor.slug}`
  const breadcrumbs = generateBreadcrumbs(url, breadcrumbLabels)

  useSEO({
    title: competitor.seoTitle,
    description: competitor.seoDescription,
    url,
    canonical: url,
    breadcrumbs,
    schema: createServiceSchema({
      name: competitor.seoTitle,
      description: competitor.seoDescription,
      url,
    }),
  })

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const tableRef = useScrollReveal<HTMLElement>('.compare-table')
  const fitRef = useScrollReveal<HTMLElement>('.fit-content')
  const stepsRef = useStaggeredGrid<HTMLDivElement>('.step-item', { stagger: 0.12 })
  const faqRef = useScrollReveal<HTMLElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLElement>('.cta-content')

  const others = competitors.filter((c) => c.slug !== competitor.slug)

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <Eyebrow centered>Fugue Lab vs {competitor.name}</Eyebrow>
        <h1 className="text-fluid-h1">{competitor.heroHeadline}</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">{competitor.heroBody}</p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call <ArrowRight className="ml-1 h-5 w-5" />
              </Button>
            </m.div>
          </a>
        </div>
      </section>

      {/* The short version */}
      <section className="mx-auto max-w-3xl space-y-4">
        <h2 className="text-fluid-h3">The short version</h2>
        <p className="text-muted-foreground text-lg leading-relaxed">{competitor.shortVersion}</p>
      </section>

      {/* Comparison table */}
      <section ref={tableRef} className="compare-table space-y-6">
        <h2 className="text-fluid-h2 text-center">Side by side</h2>
        {/* Wide table scrolls inside its own container; the page never scrolls horizontally */}
        <div className="border-border/60 overflow-x-auto rounded-2xl border">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-border/60 border-b">
                <th scope="col" className="text-muted-foreground w-1/4 p-4 font-medium"></th>
                <th scope="col" className="text-foreground bg-primary/5 w-[37.5%] p-4 text-base font-bold">
                  Fugue Lab
                </th>
                <th scope="col" className="text-foreground w-[37.5%] p-4 text-base font-bold">
                  {competitor.name}
                </th>
              </tr>
            </thead>
            <tbody>
              {competitor.rows.map((row) => (
                <tr key={row.dimension} className="border-border/40 border-b last:border-b-0">
                  <th scope="row" className="text-foreground p-4 align-top font-semibold">
                    {row.dimension}
                  </th>
                  <td className="bg-primary/5 p-4 align-top">
                    <div className="flex items-start gap-2">
                      <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                      <span>{row.fugueLab}</span>
                    </div>
                  </td>
                  <td className="text-muted-foreground p-4 align-top">{row.them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground text-center text-xs">
          Comparison reflects publicly available information at the time of writing. See{' '}
          <Link to="/pricing" className="text-primary hover:underline">
            packages
          </Link>{' '}
          for Fugue Lab pricing.
        </p>
      </section>

      {/* Where they fit */}
      <section ref={fitRef} className="fit-content mx-auto max-w-3xl space-y-4">
        <h2 className="text-fluid-h3">{competitor.whereTheyFit.heading}</h2>
        <p className="text-muted-foreground text-lg leading-relaxed">{competitor.whereTheyFit.body}</p>
      </section>

      {/* Switching */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">How switching works</h2>
        <div ref={stepsRef} className="grid gap-6 md:grid-cols-3">
          {competitor.switchSteps.map((step, idx) => (
            <div
              key={step.title}
              className="step-item bg-card border-border/70 from-primary/[0.07] rounded-2xl border bg-linear-to-b to-transparent p-6"
            >
              <div className="bg-primary/10 text-primary mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full text-lg font-bold">
                {idx + 1}
              </div>
              <h3 className="text-lg font-bold">{step.title}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section ref={faqRef} className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Questions that come up</h2>
        <div className="space-y-6">
          {competitor.faqs.map((faq) => (
            <div key={faq.question} className="faq-item bg-card border-border rounded-xl border p-6">
              <h3 className="font-bold">{faq.question}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Other comparisons */}
      <section className="mx-auto max-w-3xl space-y-4 text-center">
        <h2 className="text-fluid-h4">More comparisons</h2>
        <div className="flex flex-wrap justify-center gap-3">
          {others.map((other) => (
            <Link
              key={other.slug}
              to={`/compare/${other.slug}`}
              className="border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground rounded-full border px-4 py-2 text-sm transition"
            >
              Fugue Lab vs {other.name}
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        ref={ctaRef}
        className="cta-content from-primary/10 via-primary/5 border-primary/20 space-y-6 rounded-2xl border bg-linear-to-br to-transparent p-8 text-center md:p-12"
      >
        <h2 className="text-fluid-h3">{competitor.ctaHeading}</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">{competitor.ctaBody}</p>
        <m.a
          href="/book"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary inline-flex cursor-pointer items-center gap-2 font-medium hover:underline"
          whileHover={{ x: 4 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          Book a Free Call <ArrowRight className="h-4 w-4" />
        </m.a>
      </section>

      <FugueContactForm />
    </div>
  )
}
